/**
 * Fase 2.5 T2.5.1 — Vurderingsoppsett-editor
 *
 * Studieplan-forfatter / medlem-admin bygger sammensatt vurdering for et
 * kurs-instans: velger modus (single/weighted/all_required per K14) og legger
 * til komponenter (skriftlig, praktisk, prosjekt, muntlig, m.fl.) med rubric,
 * vekting, deadline og krav.
 *
 * Mountet fra CourseInstanceDetail på egen tab «Vurderingsoppsett» (vises kun for
 * kurs der planen ikke er single_component eller er ubestemt).
 */
import { useEffect, useState } from 'react';
import { Plus, X, Save, AlertCircle, Lock, CheckCircle, Trash2, Edit3, ExternalLink } from 'lucide-react';
import { authenticatedFetch, getUser } from '../lib/auth';
import AssessmentInfoPanel from './AssessmentInfoPanel';
import { NewRubricModal } from './RubricManagement';
import { useConfirm } from './ui';

function rubricSettingsPath(): string {
  const user = getUser();
  if (!user) return '/settings/rubrics';
  if (user.role === 'systemadmin' || user.role === 'admin') return '/settings/rubrics';
  return '/member-settings/rubrics';
}

interface Component {
  id: string;
  component_type: string;
  title: string;
  description: string | null;
  sequence: number;
  weight_pct: number | null;
  is_required: boolean;
  is_anonymous: boolean;
  deadline: string | null;
  grading_scale: string;
  pass_threshold: number | null;
  written_exam_set_id: string | null;
  rubric_id?: string | null;
}

interface Plan {
  id: string;
  course_instance_id: string;
  model: 'single_component' | 'weighted_composite' | 'all_required_composite';
  total_weight_pct: number | null;
  locked_at: string | null;
  components: Component[];
  legacy_exam_set_id?: string | null;
}

interface ExamSetSource {
  id: string;
  title: string;
  composite_mode: string | null;
  composite_template: unknown[] | null;
}

interface Rubric {
  id: string;
  title: string;
  version_number: number;
  is_locked: boolean;
  applicable_component_types: string[];
}

const COMPONENT_TYPE_LABELS: Record<string, string> = {
  written: 'Skriftlig prøve',
  project: 'Prosjektinnlevering',
  oral: 'Muntlig',
  practical: 'Praktisk demonstrasjon',
  portfolio: 'Mappe',
  observation: 'Kontinuerlig observasjon',
  reflection: 'Refleksjonsnotat',
  group: 'Gruppearbeid'
};

const MODEL_LABELS = {
  single_component: 'Enkel — én komponent (default)',
  weighted_composite: 'Vektet sammensatt — komponenter med vekting (sum=100%)',
  all_required_composite: 'Alle må bestås — hver komponent er obligatorisk'
};

interface Props {
  courseInstanceId: string;
}

export default function CompositePlanEditor({ courseInstanceId }: Props) {
  const [plan, setPlan] = useState<Plan | null>(null);
  const [sourceExamSet, setSourceExamSet] = useState<ExamSetSource | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAddComp, setShowAddComp] = useState(false);
  const [editingComp, setEditingComp] = useState<Component | null>(null);
  const [rubrics, setRubrics] = useState<Rubric[]>([]);
  const [savingModel, setSavingModel] = useState(false);
  const { confirm } = useConfirm();

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const r = await authenticatedFetch(`/api/admin/assessment-plans/${courseInstanceId}`);
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const j = await r.json();
      const loadedPlan: Plan = j.plan || j;
      setPlan(loadedPlan);

      // Last rubrics for komponent-kobling
      const rrRes = await authenticatedFetch('/api/admin/assessment-rubrics');
      if (rrRes.ok) {
        const rrJson = await rrRes.json();
        setRubrics(rrJson.rubrics || []);
      }

      // CX: hvis planen er auto-opprettet fra prøvesett-template, hent exam_set
      // for å vise "auto-opprettet"-badge og lenke til redigering av templaten.
      if (loadedPlan?.legacy_exam_set_id) {
        try {
          const esRes = await authenticatedFetch(`/api/exams/${loadedPlan.legacy_exam_set_id}`);
          if (esRes.ok) {
            const esJson = await esRes.json();
            const es = esJson.exam_set || esJson;
            if (es?.composite_mode) {
              setSourceExamSet({
                id: es.id,
                title: es.title,
                composite_mode: es.composite_mode,
                composite_template: es.composite_template || null,
              });
            } else {
              setSourceExamSet(null);
            }
          }
        } catch { /* ikke-fatal */ }
      } else {
        setSourceExamSet(null);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'unknown');
    } finally {
      setLoading(false);
    }
  }

  async function refetchRubrics(): Promise<Rubric[]> {
    const r = await authenticatedFetch('/api/admin/assessment-rubrics');
    if (!r.ok) return rubrics;
    const j = await r.json();
    const next = j.rubrics || [];
    setRubrics(next);
    return next;
  }

  useEffect(() => { load(); }, [courseInstanceId]);

  async function updateModel(newModel: Plan['model']) {
    if (!plan) return;
    if (plan.locked_at) { alert('Planen er låst. Kan ikke endre modell etter finalisering.'); return; }
    setSavingModel(true);
    try {
      const r = await authenticatedFetch(`/api/admin/assessment-plans/${courseInstanceId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ model: newModel })
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.error || `HTTP ${r.status}`);
      }
      await load();
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Ukjent feil');
    } finally {
      setSavingModel(false);
    }
  }

  async function deleteComponent(componentId: string) {
    if (!plan) return;
    if (plan.locked_at) { alert('Planen er låst.'); return; }
    const ok = await confirm({
      title: 'Slett komponent?',
      message: 'Komponenten fjernes fra vurderingsplanen. Dette kan ikke angres.',
      variant: 'danger',
      confirmLabel: 'Slett komponent',
    });
    if (!ok) return;
    try {
      const r = await authenticatedFetch(`/api/admin/assessment-plans/${courseInstanceId}/components/${componentId}`, { method: 'DELETE' });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.error || `HTTP ${r.status}`);
      }
      await load();
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Ukjent feil');
    }
  }

  async function lockPlan() {
    if (!plan) return;
    const ok = await confirm({
      title: 'Låse vurderingsplanen?',
      message: 'Etter låsing kan komponenter ikke endres eller fjernes. Dette sikrer at kandidater vurderes etter samme plan.',
      variant: 'warning',
      confirmLabel: 'Lås planen',
      consequences: [
        'Komponenter kan ikke lenger legges til eller fjernes',
        'Vekting kan ikke endres',
        'Rubric-koblinger fryses',
      ],
    });
    if (!ok) return;
    try {
      const r = await authenticatedFetch(`/api/admin/assessment-plans/${courseInstanceId}/lock`, { method: 'POST' });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        if (err.error === 'weight_sum_invalid') {
          alert(`Vekting summerer ikke til 100. Nåværende sum: ${err.current_sum}%`);
        } else {
          alert(err.error || `HTTP ${r.status}`);
        }
        return;
      }
      await load();
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Ukjent feil');
    }
  }

  if (loading) return <div className="p-6 text-gray-600">Laster vurderingsoppsett…</div>;
  if (error && !plan) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-800 rounded-lg p-4">
        <AlertCircle className="h-4 w-4 inline mr-1" /> {error}
      </div>
    );
  }
  if (!plan) return null;

  const totalWeight = plan.components.reduce((s, c) => s + (Number(c.weight_pct) || 0), 0);
  const isWeighted = plan.model === 'weighted_composite';
  const weightsValid = !isWeighted || totalWeight === 100;

  return (
    <div className="space-y-4">
      <AssessmentInfoPanel storageKey="composite_info_open" defaultOpen={false} />

      {/* CX: info-banner når planen er auto-opprettet fra kompositt-prøvesett */}
      {sourceExamSet && (
        <div className="rounded-lg border-2 border-kf-blue-300 bg-kf-blue-50 p-4">
          <div className="flex items-start gap-3">
            <ExternalLink className="h-5 w-5 text-kf-blue-700 flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <h4 className="font-semibold text-kf-blue-900">Auto-opprettet fra prøvesett-template</h4>
              <p className="text-sm text-kf-blue-800 mt-1">
                Denne vurderingsplanen ble auto-opprettet fra prøvesettet <strong>«{sourceExamSet.title}»</strong>
                {' '}(modus: {sourceExamSet.composite_mode === 'weighted_composite' ? 'vektet' : 'alle må bestås'}).
                Komponenter som endres her gjelder <strong>kun denne kursinstansen</strong> — ikke andre kurs som bruker samme prøvesett.
              </p>
              <p className="text-xs text-kf-blue-700 mt-2">
                For å endre standard-templaten slik at alle nye kurs får oppdaterte komponenter, må prøvesettet slettes og
                opprettes på nytt via veiviseren.
              </p>
            </div>
          </div>
        </div>
      )}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900">Sammensatt vurdering</h3>
            <p className="text-sm text-gray-600 mt-1">
              Definer hvordan kandidaten vurderes. Ved «Enkel» har kurset én vurderingsform — enten
              skriftlig prøve, prosjektoppgave, portfolio, muntlig, praktisk, observasjon eller
              gruppearbeid. Ved sammensatt vurdering kombineres flere vurderingsformer med vekting
              eller krav om bestått på alle.
            </p>
          </div>
          {plan.locked_at ? (
            <span className="inline-flex items-center gap-1 text-xs bg-amber-50 text-amber-800 px-2 py-1 rounded-full">
              <Lock className="h-3 w-3" /> Låst {new Date(plan.locked_at).toLocaleDateString('no-NO')}
            </span>
          ) : (
            <span className="text-xs bg-kf-blue-50 text-kf-blue-800 px-2 py-1 rounded-full">Utkast</span>
          )}
        </div>

        <div className="mt-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">Vurderings-modell</label>
          <select
            value={plan.model}
            onChange={e => updateModel(e.target.value as Plan['model'])}
            disabled={!!plan.locked_at || savingModel}
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
          >
            <option value="single_component">{MODEL_LABELS.single_component}</option>
            <option value="weighted_composite">{MODEL_LABELS.weighted_composite}</option>
            <option value="all_required_composite">{MODEL_LABELS.all_required_composite}</option>
          </select>
        </div>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 overflow-x-auto">
        <div className="p-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <div className="text-sm font-medium text-gray-900">Komponenter ({plan.components.length})</div>
            {isWeighted && (
              <div className={`text-xs mt-1 ${weightsValid ? 'text-emerald-700' : 'text-amber-700'}`}>
                Sum vekt: {totalWeight}% {weightsValid ? '✓' : `— må være 100% for å låse (${100 - totalWeight > 0 ? `mangler ${100 - totalWeight}%` : `overskudd ${totalWeight - 100}%`})`}
              </div>
            )}
          </div>
          {!plan.locked_at && (
            <button
              onClick={() => setShowAddComp(true)}
              className="inline-flex items-center gap-1 px-3 py-1.5 bg-kf-blue-600 hover:bg-kf-blue-700 text-white rounded text-sm"
            >
              <Plus className="h-4 w-4" /> Legg til komponent
            </button>
          )}
        </div>

        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase w-16">#</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tittel</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Vekt</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Vurderingsmatrise</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Frist</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {plan.components.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-6 text-center text-gray-500 text-sm">Ingen komponenter ennå.</td></tr>
            )}
            {plan.components.map(c => {
              const rubric = c.rubric_id ? rubrics.find(r => r.id === c.rubric_id) : null;
              return (
                <tr key={c.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm text-gray-500">{c.sequence + 1}</td>
                  <td className="px-4 py-3 text-sm text-gray-700">{COMPONENT_TYPE_LABELS[c.component_type] || c.component_type}</td>
                  <td className="px-4 py-3 text-sm text-gray-900">{c.title || <span className="text-gray-400">(ingen tittel)</span>}</td>
                  <td className="px-4 py-3 text-sm text-right">{c.weight_pct != null ? `${c.weight_pct}%` : '—'}</td>
                  <td className="px-4 py-3 text-xs text-gray-600">{rubric ? `${rubric.title} v${rubric.version_number}` : (c.written_exam_set_id ? 'Prøvesett' : '—')}</td>
                  <td className="px-4 py-3 text-xs text-gray-600">{c.deadline ? new Date(c.deadline).toLocaleDateString('no-NO') : '—'}</td>
                  <td className="px-4 py-3 text-right">
                    {!plan.locked_at && (
                      <div className="inline-flex gap-1">
                        <button
                          onClick={() => setEditingComp(c)}
                          className="p-1 text-kf-blue-600 hover:bg-kf-blue-50 rounded"
                          title="Rediger komponent"
                        >
                          <Edit3 className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => deleteComponent(c.id)}
                          className="p-1 text-red-600 hover:bg-red-50 rounded"
                          title="Slett komponent"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {!plan.locked_at && plan.components.length > 0 && plan.model !== 'single_component' && (
        <div className="flex justify-end">
          <button
            onClick={lockPlan}
            disabled={isWeighted && !weightsValid}
            className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm disabled:opacity-50"
          >
            <Lock className="h-4 w-4" /> Lås plan
          </button>
        </div>
      )}

      {plan.locked_at && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-sm text-emerald-900 flex items-center gap-2">
          <CheckCircle className="h-4 w-4" /> Planen er låst og klar til sensur.
        </div>
      )}

      {showAddComp && (
        <AddComponentModal
          courseInstanceId={courseInstanceId}
          existingComponents={plan.components}
          rubrics={rubrics}
          refetchRubrics={refetchRubrics}
          model={plan.model}
          onClose={() => setShowAddComp(false)}
          onCreated={() => { setShowAddComp(false); load(); }}
        />
      )}

      {editingComp && (
        <EditComponentModal
          courseInstanceId={courseInstanceId}
          component={editingComp}
          siblingComponents={plan.components.filter(c => c.id !== editingComp.id)}
          rubrics={rubrics}
          refetchRubrics={refetchRubrics}
          model={plan.model}
          onClose={() => setEditingComp(null)}
          onSaved={() => { setEditingComp(null); load(); }}
        />
      )}
    </div>
  );
}

/**
 * Beregner proporsjonal omfordeling av søsken-vekter slik at
 * (nyEgenVekt + sum(justerteSøsken)) = 100.
 * Returnerer array med { id, newWeight } — 2 desimalers presisjon.
 * Hvis alle søsken har 0 vekt: fordel likt.
 */
function redistributeWeights(
  siblings: Component[],
  newOwnWeight: number
): Array<{ id: string; oldWeight: number; newWeight: number }> {
  const target = Math.max(0, 100 - newOwnWeight);
  const siblingSum = siblings.reduce((s, c) => s + (Number(c.weight_pct) || 0), 0);
  if (siblings.length === 0) return [];
  if (siblingSum === 0) {
    const equal = target / siblings.length;
    return siblings.map(c => ({ id: c.id, oldWeight: 0, newWeight: Math.round(equal * 100) / 100 }));
  }
  const factor = target / siblingSum;
  return siblings.map(c => {
    const w = Number(c.weight_pct) || 0;
    return { id: c.id, oldWeight: w, newWeight: Math.round(w * factor * 100) / 100 };
  });
}

function AddComponentModal({
  courseInstanceId,
  existingComponents,
  rubrics,
  refetchRubrics,
  model,
  onClose,
  onCreated
}: {
  courseInstanceId: string;
  existingComponents: Component[];
  rubrics: Rubric[];
  refetchRubrics: () => Promise<Rubric[]>;
  model: string;
  onClose: () => void;
  onCreated: () => void;
}) {
  const [type, setType] = useState('written');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [weightPct, setWeightPct] = useState(50);
  const [isRequired, setIsRequired] = useState(true);
  const [isAnonymous, setIsAnonymous] = useState(true);
  const [deadline, setDeadline] = useState('');
  const [gradingScale, setGradingScale] = useState('pass_fail');
  const [passThreshold, setPassThreshold] = useState(60);
  const [rubricId, setRubricId] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showRedistributeConfirm, setShowRedistributeConfirm] = useState(false);
  const [redistribution, setRedistribution] = useState<ReturnType<typeof redistributeWeights>>([]);
  const [showNewRubric, setShowNewRubric] = useState(false);

  // Filter rubrics for valgt type
  const relevantRubrics = rubrics.filter(r => r.applicable_component_types.includes(type));

  // Auto-oppdater is_anonymous per K12
  function handleTypeChange(newType: string) {
    setType(newType);
    // K12: written/project = anonym default, oral/practical/observation/group = ikke-anonym default
    const anon = ['written', 'project', 'portfolio', 'reflection'].includes(newType);
    setIsAnonymous(anon);
    setRubricId(''); // reset rubric-valg
  }

  // Sjekk om vekten sammen med søskene bryter 100-regelen
  function computeWeightMismatch(): number | null {
    if (model !== 'weighted_composite') return null;
    const siblingSum = existingComponents.reduce((s, c) => s + (Number(c.weight_pct) || 0), 0);
    const total = siblingSum + weightPct;
    return total === 100 ? null : total;
  }

  async function persistComponent(skipRedistribute = false) {
    setSaving(true);
    setError(null);
    try {
      const existingSequences = existingComponents.map(c => c.sequence);
      const nextSeq = existingSequences.length > 0 ? Math.max(...existingSequences) + 1 : 0;
      const body: any = {
        component_type: type,
        title: title.trim(),
        description: description || null,
        sequence: nextSeq,
        weight_pct: model === 'weighted_composite' ? weightPct : null,
        is_required: isRequired,
        is_anonymous: isAnonymous,
        deadline: deadline || null,
        grading_scale: gradingScale,
        pass_threshold: passThreshold
      };
      if (rubricId) body.rubric_id = rubricId;

      const r = await authenticatedFetch(`/api/admin/assessment-plans/${courseInstanceId}/components`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.error || err.message || `HTTP ${r.status}`);
      }

      // Etter suksess: hvis omfordeling ble bekreftet, oppdater alle søsken
      if (!skipRedistribute && redistribution.length > 0) {
        for (const adj of redistribution) {
          const sib = existingComponents.find(c => c.id === adj.id);
          if (!sib) continue;
          await authenticatedFetch(`/api/admin/assessment-plans/${courseInstanceId}/components/${adj.id}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              component_type: sib.component_type,
              title: sib.title,
              description: sib.description,
              weight_pct: adj.newWeight,
              is_required: sib.is_required,
              is_anonymous: sib.is_anonymous,
              deadline: sib.deadline,
              grading_scale: sib.grading_scale,
              pass_threshold: sib.pass_threshold,
              rubric_id: sib.rubric_id
            })
          });
        }
      }
      onCreated();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'unknown');
    } finally {
      setSaving(false);
    }
  }

  async function save() {
    if (!title || title.trim().length < 2) { setError('Tittel må ha minst 2 tegn'); return; }
    const mismatch = computeWeightMismatch();
    if (mismatch !== null && existingComponents.length > 0) {
      // Beregn foreslått omfordeling og be om bekreftelse
      const proposal = redistributeWeights(existingComponents, weightPct);
      setRedistribution(proposal);
      setShowRedistributeConfirm(true);
      return;
    }
    await persistComponent(true);
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-lg max-w-lg w-full p-4 sm:p-6 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">Ny komponent</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700"><X className="h-5 w-5" /></button>
        </div>
        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Vurderings-type</label>
            <select value={type} onChange={e => handleTypeChange(e.target.value)} className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm">
              {Object.entries(COMPONENT_TYPE_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Tittel</label>
            <input value={title} onChange={e => setTitle(e.target.value)} className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" placeholder="F.eks. «Praktisk mureprøve»" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Beskrivelse (valgfri)</label>
            <textarea value={description} onChange={e => setDescription(e.target.value)} rows={2} className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" />
          </div>
          {model === 'weighted_composite' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Vekt (%)</label>
              <input type="number" min={1} max={100} value={weightPct} onChange={e => setWeightPct(parseFloat(e.target.value || '0'))} className="w-32 border border-gray-300 rounded-md px-3 py-2 text-sm" />
              <p className="text-xs text-gray-500 mt-1">Sum av alle komponenter må være 100% for å låse.</p>
            </div>
          )}
          {['practical','project','oral','portfolio','observation','reflection','group'].includes(type) && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Vurderingsmatrise for sensor</label>
              {relevantRubrics.length > 0 ? (
                <>
                  <div className="flex items-stretch gap-2">
                    <select value={rubricId} onChange={e => setRubricId(e.target.value)} className="flex-1 border border-gray-300 rounded-md px-3 py-2 text-sm">
                      <option value="">(ingen — sensor bruker fri vurdering)</option>
                      {relevantRubrics.map(r => <option key={r.id} value={r.id}>{r.title} v{r.version_number} {r.is_locked ? '(låst)' : ''}</option>)}
                    </select>
                    <button
                      type="button"
                      onClick={() => setShowNewRubric(true)}
                      className="inline-flex items-center gap-1 px-3 py-2 border border-kf-blue-300 bg-kf-blue-50 text-kf-blue-700 rounded-md text-sm hover:bg-kf-blue-100 whitespace-nowrap"
                      title="Opprett ny matrise og velg den her"
                    >
                      <Plus className="h-4 w-4" /> Opprett ny
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Matrisen gir sensor en strukturert sjekkliste når denne komponenten skal sensureres.
                    <a href={rubricSettingsPath()} target="_blank" rel="noopener noreferrer" className="text-kf-blue-600 hover:text-kf-blue-800 ml-1 inline-flex items-center gap-0.5">
                      Administrer matriser <ExternalLink className="h-3 w-3" />
                    </a>
                  </p>
                </>
              ) : (
                <div className="bg-amber-50 border border-amber-200 rounded p-3 text-xs text-amber-900 space-y-2">
                  <p>Ingen matriser er opprettet for denne komponenttypen ennå. Sensor kan uansett vurdere fritt.</p>
                  <button
                    type="button"
                    onClick={() => setShowNewRubric(true)}
                    className="inline-flex items-center gap-1 px-3 py-1.5 bg-kf-blue-600 text-white rounded-md text-xs font-medium hover:bg-kf-blue-700"
                  >
                    <Plus className="h-3.5 w-3.5" /> Opprett ny matrise
                  </button>
                  <a href={rubricSettingsPath()} target="_blank" rel="noopener noreferrer" className="ml-2 text-kf-blue-700 hover:text-kf-blue-900 inline-flex items-center gap-0.5 font-medium">
                    eller administrer alle matriser <ExternalLink className="h-3 w-3" />
                  </a>
                </div>
              )}
              {showNewRubric && (
                <NewRubricModal
                  onClose={() => setShowNewRubric(false)}
                  onCreated={async (newRubricId) => {
                    const updated = await refetchRubrics();
                    if (newRubricId && updated.some(r => r.id === newRubricId)) {
                      setRubricId(newRubricId);
                    }
                    setShowNewRubric(false);
                  }}
                />
              )}
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Karakter-skala</label>
              <select value={gradingScale} onChange={e => setGradingScale(e.target.value)} className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm">
                <option value="pass_fail">Bestått / Ikke bestått</option>
                <option value="three_tier">Meget godt bestått / Bestått / Ikke bestått</option>
                <option value="a_f">A–F</option>
                <option value="percentage">Prosent (0–100)</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Grense for bestått (%)</label>
              <input type="number" min={0} max={100} value={passThreshold} onChange={e => setPassThreshold(parseFloat(e.target.value || '0'))} className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Frist (valgfri)</label>
            <input type="datetime-local" value={deadline} onChange={e => setDeadline(e.target.value)} className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" />
          </div>
          <div className="flex items-center gap-4 text-sm">
            <label className="inline-flex items-center gap-1"><input type="checkbox" checked={isRequired} onChange={e => setIsRequired(e.target.checked)} /> Obligatorisk</label>
            <label className="inline-flex items-center gap-1">
              <input type="checkbox" checked={isAnonymous} onChange={e => setIsAnonymous(e.target.checked)} />
              Anonym vurdering
              <span className="text-xs text-gray-500 ml-1">(standard: {['written','project','portfolio','reflection'].includes(type) ? 'på' : 'av'})</span>
            </label>
          </div>
          {error && (
            <div className="bg-red-50 border border-red-200 rounded p-2 text-sm text-red-800">
              <AlertCircle className="h-4 w-4 inline mr-1" /> {error}
            </div>
          )}

          {showRedistributeConfirm && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm">
              <div className="font-medium text-amber-900 mb-2 flex items-center gap-2">
                <AlertCircle className="h-4 w-4" /> Vektsummen blir {existingComponents.reduce((s,c)=>s+(Number(c.weight_pct)||0),0) + weightPct}% — foreslår proporsjonal justering av øvrige komponenter:
              </div>
              <ul className="space-y-1 text-xs text-amber-900 mb-3">
                {redistribution.map(adj => {
                  const sib = existingComponents.find(c => c.id === adj.id);
                  return (
                    <li key={adj.id} className="flex justify-between">
                      <span>{sib?.title || COMPONENT_TYPE_LABELS[sib?.component_type || ''] || sib?.component_type}</span>
                      <span className="font-mono">{adj.oldWeight}% → {adj.newWeight}%</span>
                    </li>
                  );
                })}
              </ul>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => persistComponent(false)}
                  disabled={saving}
                  className="px-3 py-1.5 text-xs bg-emerald-600 hover:bg-emerald-700 text-white rounded disabled:opacity-50"
                >
                  {saving ? 'Lagrer…' : 'OK — juster automatisk og lagre'}
                </button>
                <button
                  onClick={() => { setShowRedistributeConfirm(false); setRedistribution([]); }}
                  className="px-3 py-1.5 text-xs text-amber-900 hover:bg-amber-100 rounded"
                >
                  Endre vekt selv
                </button>
              </div>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <button onClick={onClose} className="px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded">Avbryt</button>
            <button onClick={save} disabled={saving || showRedistributeConfirm} className="px-3 py-2 text-sm bg-kf-blue-600 hover:bg-kf-blue-700 text-white rounded disabled:opacity-50 inline-flex items-center gap-2">
              <Save className="h-4 w-4" /> {saving ? 'Lagrer…' : 'Legg til'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}


function EditComponentModal({
  courseInstanceId,
  component,
  siblingComponents,
  rubrics,
  refetchRubrics,
  model,
  onClose,
  onSaved
}: {
  courseInstanceId: string;
  component: Component;
  siblingComponents: Component[];
  rubrics: Rubric[];
  refetchRubrics: () => Promise<Rubric[]>;
  model: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [type, setType] = useState(component.component_type);
  const [title, setTitle] = useState(component.title || '');
  const [description, setDescription] = useState(component.description || '');
  const [weightPct, setWeightPct] = useState<number>(Number(component.weight_pct) || 0);
  const [isRequired, setIsRequired] = useState(component.is_required);
  const [isAnonymous, setIsAnonymous] = useState(component.is_anonymous);
  const [deadline, setDeadline] = useState(component.deadline ? component.deadline.slice(0, 16) : '');
  const [gradingScale, setGradingScale] = useState(component.grading_scale || 'pass_fail');
  const [passThreshold, setPassThreshold] = useState<number>(Number(component.pass_threshold) || 60);
  const [rubricId, setRubricId] = useState(component.rubric_id || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showRedistributeConfirm, setShowRedistributeConfirm] = useState(false);
  const [redistribution, setRedistribution] = useState<ReturnType<typeof redistributeWeights>>([]);
  const [showNewRubric, setShowNewRubric] = useState(false);

  const relevantRubrics = rubrics.filter(r => r.applicable_component_types.includes(type));

  function handleTypeChange(newType: string) {
    setType(newType);
    setRubricId('');
  }

  function computeWeightMismatch(): number | null {
    if (model !== 'weighted_composite') return null;
    const siblingSum = siblingComponents.reduce((s, c) => s + (Number(c.weight_pct) || 0), 0);
    const total = siblingSum + weightPct;
    return total === 100 ? null : total;
  }

  async function persistComponent() {
    setSaving(true);
    setError(null);
    try {
      const body: any = {
        component_type: type,
        title: title.trim(),
        description: description || null,
        weight_pct: model === 'weighted_composite' ? weightPct : null,
        is_required: isRequired,
        is_anonymous: isAnonymous,
        deadline: deadline || null,
        grading_scale: gradingScale,
        pass_threshold: passThreshold,
        rubric_id: rubricId || null
      };

      const r = await authenticatedFetch(`/api/admin/assessment-plans/${courseInstanceId}/components/${component.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.error || err.message || `HTTP ${r.status}`);
      }

      // Justér søsken hvis omfordeling ble bekreftet
      if (redistribution.length > 0) {
        for (const adj of redistribution) {
          const sib = siblingComponents.find(c => c.id === adj.id);
          if (!sib) continue;
          await authenticatedFetch(`/api/admin/assessment-plans/${courseInstanceId}/components/${adj.id}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              component_type: sib.component_type,
              title: sib.title,
              description: sib.description,
              weight_pct: adj.newWeight,
              is_required: sib.is_required,
              is_anonymous: sib.is_anonymous,
              deadline: sib.deadline,
              grading_scale: sib.grading_scale,
              pass_threshold: sib.pass_threshold,
              rubric_id: sib.rubric_id
            })
          });
        }
      }
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'unknown');
    } finally {
      setSaving(false);
    }
  }

  async function save() {
    if (!title || title.trim().length < 2) { setError('Tittel må ha minst 2 tegn'); return; }
    const mismatch = computeWeightMismatch();
    if (mismatch !== null && siblingComponents.length > 0) {
      const proposal = redistributeWeights(siblingComponents, weightPct);
      setRedistribution(proposal);
      setShowRedistributeConfirm(true);
      return;
    }
    await persistComponent();
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-lg max-w-lg w-full p-4 sm:p-6 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">Rediger komponent</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700"><X className="h-5 w-5" /></button>
        </div>
        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Vurderings-type</label>
            <select value={type} onChange={e => handleTypeChange(e.target.value)} className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm">
              {Object.entries(COMPONENT_TYPE_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Tittel</label>
            <input value={title} onChange={e => setTitle(e.target.value)} className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Beskrivelse (valgfri)</label>
            <textarea value={description} onChange={e => setDescription(e.target.value)} rows={2} className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" />
          </div>
          {model === 'weighted_composite' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Vekt (%)</label>
              <input type="number" min={1} max={100} value={weightPct} onChange={e => setWeightPct(parseFloat(e.target.value || '0'))} className="w-32 border border-gray-300 rounded-md px-3 py-2 text-sm" />
              <p className="text-xs text-gray-500 mt-1">Sum av alle komponenter må være 100% for å låse.</p>
            </div>
          )}
          {['practical','project','oral','portfolio','observation','reflection','group'].includes(type) && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Vurderingsmatrise for sensor</label>
              {relevantRubrics.length > 0 ? (
                <>
                  <div className="flex items-stretch gap-2">
                    <select value={rubricId} onChange={e => setRubricId(e.target.value)} className="flex-1 border border-gray-300 rounded-md px-3 py-2 text-sm">
                      <option value="">(ingen — sensor bruker fri vurdering)</option>
                      {relevantRubrics.map(r => <option key={r.id} value={r.id}>{r.title} v{r.version_number} {r.is_locked ? '(låst)' : ''}</option>)}
                    </select>
                    <button
                      type="button"
                      onClick={() => setShowNewRubric(true)}
                      className="inline-flex items-center gap-1 px-3 py-2 border border-kf-blue-300 bg-kf-blue-50 text-kf-blue-700 rounded-md text-sm hover:bg-kf-blue-100 whitespace-nowrap"
                      title="Opprett ny matrise og velg den her"
                    >
                      <Plus className="h-4 w-4" /> Opprett ny
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Matrisen gir sensor en strukturert sjekkliste når denne komponenten skal sensureres.
                    <a href={rubricSettingsPath()} target="_blank" rel="noopener noreferrer" className="text-kf-blue-600 hover:text-kf-blue-800 ml-1 inline-flex items-center gap-0.5">
                      Administrer matriser <ExternalLink className="h-3 w-3" />
                    </a>
                  </p>
                </>
              ) : (
                <div className="bg-amber-50 border border-amber-200 rounded p-3 text-xs text-amber-900 space-y-2">
                  <p>Ingen matriser er opprettet for denne komponenttypen ennå. Sensor kan uansett vurdere fritt.</p>
                  <button
                    type="button"
                    onClick={() => setShowNewRubric(true)}
                    className="inline-flex items-center gap-1 px-3 py-1.5 bg-kf-blue-600 text-white rounded-md text-xs font-medium hover:bg-kf-blue-700"
                  >
                    <Plus className="h-3.5 w-3.5" /> Opprett ny matrise
                  </button>
                  <a href={rubricSettingsPath()} target="_blank" rel="noopener noreferrer" className="ml-2 text-kf-blue-700 hover:text-kf-blue-900 inline-flex items-center gap-0.5 font-medium">
                    eller administrer alle matriser <ExternalLink className="h-3 w-3" />
                  </a>
                </div>
              )}
              {showNewRubric && (
                <NewRubricModal
                  onClose={() => setShowNewRubric(false)}
                  onCreated={async (newRubricId) => {
                    const updated = await refetchRubrics();
                    if (newRubricId && updated.some(r => r.id === newRubricId)) {
                      setRubricId(newRubricId);
                    }
                    setShowNewRubric(false);
                  }}
                />
              )}
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Karakter-skala</label>
              <select value={gradingScale} onChange={e => setGradingScale(e.target.value)} className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm">
                <option value="pass_fail">Bestått / Ikke bestått</option>
                <option value="three_tier">Meget godt bestått / Bestått / Ikke bestått</option>
                <option value="a_f">A–F</option>
                <option value="percentage">Prosent (0–100)</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Grense for bestått (%)</label>
              <input type="number" min={0} max={100} value={passThreshold} onChange={e => setPassThreshold(parseFloat(e.target.value || '0'))} className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Frist (valgfri)</label>
            <input type="datetime-local" value={deadline} onChange={e => setDeadline(e.target.value)} className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" />
          </div>
          <div className="space-y-2">
            <label className="inline-flex items-center gap-2 text-sm">
              <input type="checkbox" checked={isRequired} onChange={e => setIsRequired(e.target.checked)} />
              <span><strong>Obligatorisk</strong> — kandidaten må fullføre denne komponenten</span>
            </label>
            <div className="rounded border border-kf-gray-200 bg-kf-gray-50 p-3">
              <label className="flex items-start gap-2 cursor-pointer">
                <input type="checkbox" className="mt-0.5" checked={isAnonymous} onChange={e => setIsAnonymous(e.target.checked)} />
                <span className="flex-1 text-sm">
                  <span className="font-medium text-kf-gray-900 block">
                    Anonym vurdering (sensor ser ikke kandidatens navn)
                  </span>
                  <span className="text-xs text-kf-gray-600 block mt-1">
                    Sensor får bare kandidatens anonyme kode — navn og identitet er skjult til etter karaktersetting.
                    Gir mer rettferdig vurdering og reduserer risiko for ubevisst forskjellsbehandling.
                  </span>
                  <span className="text-xs text-kf-gray-500 block mt-1 italic">
                    Anbefalt for: skriftlige prøver, prosjektoppgaver, mappevurdering, refleksjonsnotater.
                    Ikke relevant for: muntlig, praktisk, observasjon, gruppearbeid (sensor må møte kandidaten).
                  </span>
                  <span className="text-xs text-kf-gray-500 block mt-0.5">
                    Standard for {type}: {['written','project','portfolio','reflection'].includes(type) ? 'på' : 'av'}
                  </span>
                </span>
              </label>
            </div>
          </div>
          {error && (
            <div className="bg-red-50 border border-red-200 rounded p-2 text-sm text-red-800">
              <AlertCircle className="h-4 w-4 inline mr-1" /> {error}
            </div>
          )}

          {showRedistributeConfirm && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm">
              <div className="font-medium text-amber-900 mb-2 flex items-center gap-2">
                <AlertCircle className="h-4 w-4" /> Vektsummen blir {siblingComponents.reduce((s,c)=>s+(Number(c.weight_pct)||0),0) + weightPct}% — foreslår proporsjonal justering av øvrige komponenter:
              </div>
              <ul className="space-y-1 text-xs text-amber-900 mb-3">
                {redistribution.map(adj => {
                  const sib = siblingComponents.find(c => c.id === adj.id);
                  return (
                    <li key={adj.id} className="flex justify-between">
                      <span>{sib?.title || COMPONENT_TYPE_LABELS[sib?.component_type || ''] || sib?.component_type}</span>
                      <span className="font-mono">{adj.oldWeight}% → {adj.newWeight}%</span>
                    </li>
                  );
                })}
              </ul>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => persistComponent()}
                  disabled={saving}
                  className="px-3 py-1.5 text-xs bg-emerald-600 hover:bg-emerald-700 text-white rounded disabled:opacity-50"
                >
                  {saving ? 'Lagrer…' : 'OK — juster automatisk og lagre'}
                </button>
                <button
                  onClick={() => { setShowRedistributeConfirm(false); setRedistribution([]); }}
                  className="px-3 py-1.5 text-xs text-amber-900 hover:bg-amber-100 rounded"
                >
                  Endre vekt selv
                </button>
              </div>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <button onClick={onClose} className="px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded">Avbryt</button>
            <button onClick={save} disabled={saving || showRedistributeConfirm} className="px-3 py-2 text-sm bg-kf-blue-600 hover:bg-kf-blue-700 text-white rounded disabled:opacity-50 inline-flex items-center gap-2">
              <Save className="h-4 w-4" /> {saving ? 'Lagrer…' : 'Lagre endringer'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
